import React from 'react';

import { connect, useDispatch, useSelector } from 'react-redux';
import { addAlert } from 'store/actions/alert/alerts';
import {
  addFeaturesToLayerUsingGeojsonGeometry,
  clearMapLayer,
  createLayer,
} from 'utils/map/layers';
import {
  addLayerToMap,
  getAllClickedFeatures,
  getAllFeaturesByDetail,
  removeComponentLayers,
  zoomToGeojsonGeometryWithBuffer,
} from 'utils/map/map';
import PropTypes from 'prop-types';
import { authDelRequestWithParam } from 'helpers/apiRequests';
import { hasObjectKeys } from 'utils/main';
import CustomizedTable from 'components/common/components/tablev2/CustomizedTable';
import Filters from 'components/common/components/filters/Filtersv2';
import useStyles from 'consts/styles/RightScreenPart';
import CircleButton from 'components/common/components/CircleButton';
import Icons from 'consts/icons';
import { removeApprovedArea } from 'store/actions/map/approvedAreas';
import { filterZonesByFilterObject } from 'utils/filters';
import PermissionsGate from 'components/common/PermissionGate';
import { SCOPES } from 'consts/permission-maps';
import { SidebarMenuDetailsUseStyles } from 'components/sidebar/SidebarMenuDetailsUseStyles';
import { userStyles } from 'consts/styles/User';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import DisplayAreaDetails from './DisplayAreaDetails';
import EditArea from './manageApprovedAreas/edit/Main';
import AddArea from './manageApprovedAreas/add/Main';
import { addApprovedAreasToLayer, changeObjectHeightUnit } from './utils/main';

const ApprovedAreasList = props => {
  const { heightUnit } = props;

  const approvedAreas = useSelector(state => state.approvedAreas);
  const subtitles = useSelector(state => state.language).alerts;
  const map = useSelector(state => state.map);
  const closeSubtitles = useSelector(state => state.language).close_;

  const [openModal, setOpenModal] = React.useState(false);
  const [zoneToEdit, setZoneToEdit] = React.useState(null);
  const [totalRows, setTotalRows] = React.useState(0);
  const [areas, setAreas] = React.useState([]);
  const [idItemOnClick, setIdItemOnClick] = React.useState(null);
  const [selectedArea, setSelectedArea] = React.useState(null);
  const [filtersDataObject, setFiltersDataObject] = React.useState(null);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [currentPage, setCurrentPage] = React.useState(0);

  const layer = React.useRef(null);

  const filtersType = ['name', 'type', 'minHeight', 'maxHeight'];
  const dropdownOptionsToSpecifyKeyValuesToFilter = {
    type: [...new Set(areas.map(i => i.type))],
  };
  const classes = useStyles();
  const classesSidebar = SidebarMenuDetailsUseStyles();
  const classesUser = userStyles();

  const dispatch = useDispatch();

  const initMap = () => {
    if (!hasObjectKeys(map)) return;
    addLayerToMap(map, layer.current);
    map.on('click', handleClick);
    return () => cleanup();
  };

  React.useEffect(() => {
    layer.current = createLayer('areasLayer');
  }, []);

  React.useEffect(initMap, [map]);

  React.useEffect(() => {
    handleZonesChange();
  }, [approvedAreas]);

  React.useEffect(() => {
    handleFilterAreas(filtersDataObject);
  }, [filtersDataObject, rowsPerPage, currentPage, heightUnit]);

  React.useEffect(() => {
    if (!selectedArea) {
      clearMapLayer(layer.current);
      handleAllObjectVisible();
    } else {
      selectRow(selectedArea);
    }
  }, [selectedArea]);

  React.useEffect(() => {
    if (!selectedArea) return handleAllObjectVisible();
    setSelectedArea(areas.find(a => a.uid === selectedArea.uid));
  }, [areas]);

  const handleClick = event => {
    setIdItemOnClick(
      getAllFeaturesByDetail(
        getAllClickedFeatures(map, event),
        'approvedArea'
      )[0]?.id_.substring(0, 36)
    );
  };

  React.useEffect(() => {
    if (idItemOnClick !== null) {
      setSelectedArea(areas.find(area => area.uid === idItemOnClick));
    }
    return () => setIdItemOnClick(null);
  }, [idItemOnClick]);

  const cleanup = () => {
    map.un('click', handleClick);
    removeComponentLayers(map);
  };

  const handleAllObjectVisible = () => {
    if (areas) {
      addApprovedAreasToLayer(layer.current, areas);
    }
  };

  const handleZonesChange = () => {
    setTotalRows(approvedAreas.length);
    const copy = [...approvedAreas];
    clearMapLayer(layer.current);
    setAreas(
      changeObjectHeightUnit(
        JSON.parse(
          JSON.stringify(copy.splice(currentPage * rowsPerPage, rowsPerPage))
        ),
        heightUnit === 'm'
      )
    );
  };

  const handleFilterAreas = () => {
    if (!areas.length) return;
    const filteredZones = filterZonesByFilterObject(
      [...approvedAreas],
      filtersDataObject
    );
    setAreas(
      changeObjectHeightUnit(
        JSON.parse(
          JSON.stringify(
            filteredZones.splice(currentPage * rowsPerPage, rowsPerPage)
          )
        ),
        heightUnit === 'm'
      )
    );
    setTotalRows(filteredZones.length);
  };

  const removeArea = async uid => {
    const result = await authDelRequestWithParam('areas', uid, {
      active: false,
    });
    if (result.status === 204) {
      dispatch(removeApprovedArea(uid));
      dispatch(
        addAlert({
          title: subtitles.addDataSuccess.title_,
          desc: subtitles.addDataSuccess.desc_,
          type: 'success',
        })
      );
    } else
      dispatch(
        addAlert({
          title: subtitles[result.data.code].title_,
          desc: subtitles[result.data.code].desc_,
          type: 'error',
        })
      );
  };

  const zoom = geojson => {
    zoomToGeojsonGeometryWithBuffer(map, geojson);
  };

  const selectRow = (rowData, index = null) => {
    const area = index === null ? rowData : areas[index];
    if (area?.name !== rowData.name) return;
    setSelectedArea(area);
    addFeaturesToLayerUsingGeojsonGeometry(layer.current, area.geojson);
  };

  return (
    <>
      <div
        className={`${classesSidebar.SidebarMenuDetails}__table__details__filters`}
      >
        <Filters
          filters={filtersType}
          setFiltersData={setFiltersDataObject}
          dropdownOptionsToSpecifyKeyValuesToFilter={
            dropdownOptionsToSpecifyKeyValuesToFilter
          }
        />
      </div>
      <div>
        {zoneToEdit || openModal ? null : (
          <PermissionsGate scopes={[SCOPES.canAddCorridor]}>
            <CircleButton
              position={{ right: 30, bottom: 20, position: 'absolute' }}
              size={25}
              PassedIcon={<Icons.Actions.Add2 />}
              onClickFunction={() => setOpenModal(!openModal)}
            />
          </PermissionsGate>
        )}
      </div>
      <div className={`${classes.Wrapper}__history-wrapper-short`}>
        <CustomizedTable
          headers={[
            'Name',
            'Type',
            `AGL min [${heightUnit}]`,
            `AGL max [${heightUnit}]`,
            'Active',
          ]}
          data={areas.map(area => ({
            name: area.name,
            type: area.type,
            heightMin: area.agl_min,
            height: area.agl_max,
            active: area.active,
          }))}
          onClickFunction={selectRow}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          total={totalRows}
          selectedRow={selectedArea}
          filtersUrl="corridorsReport"
          filterAdd={!zoneToEdit}
        />
      </div>
      {selectedArea ? (
        <Grid
          container
          className={`${classesUser.User}__dashboard ${classesUser.User}`}
        >
          <Tooltip title={closeSubtitles} placement="bottom">
            <div
              className={`${classesUser.User}__settings__close`}
              onClick={() => setSelectedArea(null)}
              onKeyPress={() => setSelectedArea(null)}
              role="button"
              tabIndex={0}
            >
              <div
                className={`${classesUser.User}__settings__close__container`}
              >
                <Icons.Actions.Close />
              </div>
            </div>
          </Tooltip>
          <DisplayAreaDetails
            areaToDisplay={selectedArea}
            setSelectedArea={setSelectedArea}
            zoom={zoom}
            edit={setZoneToEdit}
            remove={removeArea}
          />
        </Grid>
      ) : null}
      {!!openModal && (
        <AddArea
          getToPreviousScreen={() => setOpenModal(false)}
          category="corridor"
        />
      )}
      {!!zoneToEdit && (
        <EditArea
          getToPreviousScreen={() => setZoneToEdit(null)}
          selectedArea={zoneToEdit}
        />
      )}
    </>
  );
};

const mapStateToProps = state => ({
  heightUnit: state.heightUnit,
});

ApprovedAreasList.propTypes = {
  heightUnit: PropTypes.string.isRequired,
};

export default connect(mapStateToProps)(ApprovedAreasList);
